import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, ClipboardList } from "lucide-react";
import { getChecklist } from "../data/moduleChecklists";

/**
 * ModuleChecklist — a to-do list per module. Task defs live on the frontend
 * (`/app/frontend/src/data/moduleChecklists.js`); backend persists which
 * task_ids the user has checked in db.user_progress.checked_task_ids.
 *
 * The parent (`ModuleDetail`) conditionally renders this component per tab, so
 * on tab switch it unmounts and loses local state. To keep the checked state
 * consistent across tab switches, the parent owns `initialChecked` via
 * `onChange` — whenever the user toggles, we push the latest Set of ids up so
 * the parent's copy stays in sync and re-renders with the correct state.
 */
export default function ModuleChecklist({ slug, initialChecked = [], onChange }) {
  const tasks = getChecklist(slug);
  const [checked, setChecked] = useState(() => new Set(initialChecked));
  const [busyIds, setBusyIds] = useState(new Set());

  // Sync incoming `initialChecked` (parent source-of-truth) into local state
  // on remount / slug change. Using a stable key (sorted+joined) avoids
  // unnecessary re-syncs from prop identity changes on re-render.
  const initialKey = useMemo(
    () => [...(initialChecked || [])].sort().join("|"),
    [initialChecked]
  );
  useEffect(() => {
    setChecked(new Set(initialChecked || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey, slug]);

  const done = useMemo(() => tasks.filter((t) => checked.has(t.id)).length, [tasks, checked]);
  const total = tasks.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  if (!total) {
    return (
      <div className="surface p-10 text-center max-w-2xl" data-testid="checklist-empty">
        <ClipboardList className="w-6 h-6 text-muted-foreground mx-auto" />
        <p className="mt-3 font-display text-xl">Checklist coming soon</p>
        <p className="text-sm text-muted-foreground mt-2">
          The actionable to-dos for this module haven't been published yet.
        </p>
      </div>
    );
  }

  const toggle = async (task) => {
    const isChecked = checked.has(task.id);
    // Optimistic update
    const next = new Set(checked);
    if (isChecked) next.delete(task.id); else next.add(task.id);
    setChecked(next);
    onChange?.([...next]);
    setBusyIds((s) => new Set(s).add(task.id));
    try {
      await api.put(`/modules/${slug}/checklist`, { task_id: task.id, checked: !isChecked });
    } catch (err) {
      // Revert on failure
      const revert = new Set(checked);
      if (isChecked) revert.add(task.id); else revert.delete(task.id);
      setChecked(revert);
      onChange?.([...revert]);
      toast.error(formatError(err));
    } finally {
      setBusyIds((s) => { const c = new Set(s); c.delete(task.id); return c; });
    }
  };

  return (
    <div className="max-w-2xl" data-testid={`checklist-${slug}`}>
      <div className="surface p-6 mb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="chip-amber inline-flex items-center gap-1">
              <ClipboardList className="w-3 h-3" /> Module to-dos
            </p>
            <h2 className="font-display text-2xl mt-2">Action checklist</h2>
            <p className="text-sm text-muted-foreground mt-1">
              The concrete tasks the module recommends. Tick them off as you go — your progress is saved automatically.
            </p>
          </div>
          <div className="text-right">
            <p className="font-display text-3xl" data-testid="checklist-count">
              {done}<span className="text-muted-foreground text-xl">/{total}</span>
            </p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {pct}% complete
            </p>
          </div>
        </div>
        <div className="mt-4 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-shell-accent transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="checklist-progress-bar"
          />
        </div>
      </div>

      <div className="grid gap-2">
        {tasks.map((t, i) => {
          const isChecked = checked.has(t.id);
          const isBusy = busyIds.has(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => toggle(t)}
              disabled={isBusy}
              data-testid={`checklist-item-${t.id}`}
              className={[
                "surface p-4 flex items-start gap-3 text-left transition-all",
                isChecked ? "opacity-75" : "hover:-translate-y-0.5",
                isBusy ? "opacity-50" : "",
              ].join(" ")}
            >
              <span className={[
                "w-7 h-7 rounded-full grid place-items-center shrink-0 transition-colors mt-0.5",
                isChecked ? "bg-green-500 text-white" : "bg-white border-2 border-shell-border",
              ].join(" ")}>
                {isChecked ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4 text-transparent" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className={[
                  "text-sm leading-snug",
                  isChecked ? "line-through text-muted-foreground" : "text-foreground font-medium",
                ].join(" ")}>
                  {t.label}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 pt-1">
                {String(i + 1).padStart(2, "0")}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
